import { Badge, CounterBadge, mergeClasses } from "@fluentui/react-components";
import { useFragment } from "@nova/react";
import React from "react";
import type { TileBadge_badge$data } from "./__generated__/TileBadge_badge.types.graphql";
import { useBadgeStyles } from "./Badge.styles";
import { ViewMode } from "./Constants";
import { useTileBadgeStyles } from "./TileBadge.styles";
import {
  fragment as M365ApplicationBadge,
  TileBadgeProps,
} from "./TileBadge.types";

/**
 * Launch Tile Badge React functional component.
 *
 */
export const TileBadge: React.FC<TileBadgeProps> = ({
  badge: badgeRef,
  transparentBorder,
  readerBoxId,
  viewMode,
  isDragging,
  enableMercuryDesignStyles,
}) => {
  // Retrieves badge value from GQL fragment
  const badgeFragment: TileBadge_badge$data = useFragment(
    M365ApplicationBadge,
    badgeRef
  );
  // render the badge
  const tileBadgeStyles = useTileBadgeStyles();
  const commonBadgeStyles = useBadgeStyles();
  const badgeStyles = { ...tileBadgeStyles, ...commonBadgeStyles };
  const badgeElement = React.useMemo(() => {
    let badgeElement: JSX.Element | null = null;
    if (badgeFragment.__typename === "M365ApplicationNumericBadge") {
      const numericBadge = badgeFragment as {
        count: number;
        countDescription: string;
      };
      const numericBadgeClassNames = mergeClasses(
        badgeStyles.badgeWide,
        !!transparentBorder &&
          badgeStyles.transparentBorder &&
          badgeStyles.transparentBorder,
        viewMode === ViewMode.Flyout && badgeStyles.flyoutBadge,
        viewMode === ViewMode.Default && badgeStyles.defaultBadge,
        viewMode === ViewMode.Default &&
          enableMercuryDesignStyles &&
          badgeStyles.mercuryDesignBadge,
        isDragging && badgeStyles.draggingBadge,
        enableMercuryDesignStyles &&
          numericBadge.count > 99 &&
          badgeStyles.mercuryDesignWideBadge
      );
      badgeElement = (
        <>
          <CounterBadge
            size="small"
            shape="circular"
            color="danger"
            appearance="filled"
            count={numericBadge.count}
            className={numericBadgeClassNames}
          />
          <div
            className={badgeStyles.badgeReaderBox}
            aria-hidden="true"
            id={readerBoxId}
          >
            {numericBadge.countDescription}
          </div>
        </>
      );
    } else if (badgeFragment.__typename === "M365ApplicationDotBadge") {
      const dotBadge = badgeFragment as {
        description: string;
      };
      const dotBadgeClassNames = mergeClasses(
        badgeStyles.dotBadgeCommon,
        !!transparentBorder &&
          badgeStyles.transparentBorder &&
          badgeStyles.transparentBorder,
        viewMode === ViewMode.Flyout && badgeStyles.flyoutDotBadge,
        viewMode === ViewMode.Default && badgeStyles.defaultDotBadge,
        viewMode === ViewMode.Default &&
          enableMercuryDesignStyles &&
          badgeStyles.mercuryDesignDotBadge,
        isDragging && badgeStyles.draggingBadge
      );
      badgeElement = (
        <>
          <CounterBadge
            size="small"
            shape="circular"
            color="danger"
            appearance="filled"
            count={undefined}
            dot={true}
            className={dotBadgeClassNames}
          />
          <div
            className={badgeStyles.badgeReaderBox}
            aria-hidden="true"
            id={readerBoxId}
          >
            {dotBadge.description}
          </div>
        </>
      );
    } else if (
      badgeFragment.__typename === "M365ApplicationStringBadge" &&
      viewMode === ViewMode.Flyout
    ) {
      const stringBadge = badgeFragment as {
        text: string;
        textDescription: string;
      };
      const stringBadgeClassNames = mergeClasses(
        badgeStyles.badgeWide,
        !!transparentBorder &&
          badgeStyles.transparentBorder &&
          badgeStyles.transparentBorder,
        badgeStyles.flyoutBadge,
        badgeStyles.stringBadge,
        enableMercuryDesignStyles &&
          stringBadge.text.length > 2 &&
          badgeStyles.mercuryDesignWideBadge
      );
      badgeElement = (
        <>
          <Badge
            size="small"
            shape="circular"
            color="brand"
            appearance="filled"
            className={stringBadgeClassNames}
          >
            {stringBadge.text}
          </Badge>
          <div
            className={badgeStyles.badgeReaderBox}
            aria-hidden="true"
            id={readerBoxId}
          >
            {stringBadge.textDescription}
          </div>
        </>
      );
    }
    return badgeElement;
  }, [
    badgeFragment,
    viewMode,
    badgeStyles.badgeWide,
    badgeStyles.transparentBorder,
    badgeStyles.flyoutBadge,
    badgeStyles.defaultBadge,
    badgeStyles.mercuryDesignBadge,
    badgeStyles.draggingBadge,
    badgeStyles.badgeReaderBox,
    badgeStyles.dotBadgeCommon,
    badgeStyles.flyoutDotBadge,
    badgeStyles.defaultDotBadge,
    badgeStyles.mercuryDesignDotBadge,
    badgeStyles.stringBadge,
    badgeStyles.mercuryDesignWideBadge,
    transparentBorder,
    enableMercuryDesignStyles,
    isDragging,
    readerBoxId,
  ]);
  return badgeElement;
};
